import { openAIBaseUrl } from "./state";

// Deployment wizard data: model/GPU catalogs, memory estimate and API payload.
// Mirrors omnimizer/api/src/schemas/deployments.py (LLMConfig, DynamoConfig)
// and omnimizer/dev/ui/src/lib/catalog.ts; keep them in sync.

// Parsers accepted by LLMConfig; Dynamo enables automatic tool choice with them.
export const TOOL_CALL_PARSERS = ["qwen3_coder", "glm47"] as const;
export const REASONING_PARSERS = ["qwen3", "glm45"] as const;

export type ModelArchitecture = {
  layers: number;
  kvHeads: number;
  headDim: number;
};

export type CatalogModel = {
  id: string;
  label: string;
  family: string;
  hfPath: string;
  parameters: string;
  // Checkpoint size as served (bf16/fp16 unless prequantized).
  weightsGb: number;
  maxContext: number;
  architecture: ModelArchitecture;
  // vLLM detects the method from the checkpoint's quantization_config.
  prequantized?: "awq" | "gptq";
  toolCallParser?: (typeof TOOL_CALL_PARSERS)[number];
  reasoningParser?: (typeof REASONING_PARSERS)[number];
  description: string;
  source: "omnimizer" | "organization" | "custom";
};

// Public, ungated Hugging Face checkpoints; architecture values from each config.json.
export const MODEL_CATALOG: CatalogModel[] = [
  {
    id: "smollm2-135m",
    label: "SmolLM2 135M Instruct",
    family: "SmolLM2",
    hfPath: "HuggingFaceTB/SmolLM2-135M-Instruct",
    parameters: "135M",
    weightsGb: 0.27,
    maxContext: 8192,
    architecture: { layers: 30, kvHeads: 3, headDim: 64 },
    description: "Smoke-test model validated on the local T2000 stack.",
    source: "omnimizer",
  },
  {
    id: "smollm2-1.7b",
    label: "SmolLM2 1.7B Instruct",
    family: "SmolLM2",
    hfPath: "HuggingFaceTB/SmolLM2-1.7B-Instruct",
    parameters: "1.7B",
    weightsGb: 3.4,
    maxContext: 8192,
    architecture: { layers: 24, kvHeads: 32, headDim: 64 },
    description: "Small English assistant for low-cost internal tools.",
    source: "omnimizer",
  },
  {
    id: "qwen2.5-1.5b",
    label: "Qwen2.5 1.5B Instruct",
    family: "Qwen2.5",
    hfPath: "Qwen/Qwen2.5-1.5B-Instruct",
    parameters: "1.5B",
    weightsGb: 3.1,
    maxContext: 32768,
    architecture: { layers: 28, kvHeads: 2, headDim: 128 },
    description: "Multilingual (incl. Arabic), fits a single L4.",
    source: "omnimizer",
  },
  {
    id: "qwen2.5-7b",
    label: "Qwen2.5 7B Instruct",
    family: "Qwen2.5",
    hfPath: "Qwen/Qwen2.5-7B-Instruct",
    parameters: "7.6B",
    weightsGb: 15.2,
    maxContext: 32768,
    architecture: { layers: 28, kvHeads: 4, headDim: 128 },
    description: "General-purpose multilingual assistant.",
    source: "omnimizer",
  },
  {
    id: "qwen2.5-7b-awq",
    label: "Qwen2.5 7B Instruct AWQ",
    family: "Qwen2.5",
    hfPath: "Qwen/Qwen2.5-7B-Instruct-AWQ",
    parameters: "7.6B · 4-bit",
    weightsGb: 5.6,
    maxContext: 32768,
    architecture: { layers: 28, kvHeads: 4, headDim: 128 },
    prequantized: "awq",
    description: "Same model, AWQ 4-bit: about a third of the GPU memory.",
    source: "omnimizer",
  },
  {
    id: "qwen3-8b",
    label: "Qwen3 8B",
    family: "Qwen3",
    hfPath: "Qwen/Qwen3-8B",
    parameters: "8.2B",
    weightsGb: 16.4,
    maxContext: 40960,
    architecture: { layers: 36, kvHeads: 8, headDim: 128 },
    reasoningParser: "qwen3",
    description: "Hybrid thinking model; reasoning is returned separately.",
    source: "omnimizer",
  },
  {
    id: "phi-3.5-mini",
    label: "Phi-3.5 mini Instruct",
    family: "Phi",
    hfPath: "microsoft/Phi-3.5-mini-instruct",
    parameters: "3.8B",
    weightsGb: 7.6,
    maxContext: 131072,
    architecture: { layers: 32, kvHeads: 32, headDim: 96 },
    description: "Long-context reasoning model (up to 128k tokens).",
    source: "omnimizer",
  },
];

export type GpuSpec = {
  memoryGb: number;
  label: string;
};

// Keys match cluster supported_gpu_types values.
export const GPU_CATALOG: Record<string, GpuSpec> = {
  "nvidia-t2000": { label: "Quadro T2000", memoryGb: 4 },
  "nvidia-t4": { label: "T4", memoryGb: 16 },
  "nvidia-rtx-5070": { label: "RTX 5070", memoryGb: 12 },
  "nvidia-rtx-4090": { label: "RTX 4090", memoryGb: 24 },
  "nvidia-rtx-5090": { label: "RTX 5090", memoryGb: 32 },
  "nvidia-l4": { label: "L4", memoryGb: 24 },
  "nvidia-a10": { label: "A10", memoryGb: 24 },
  "nvidia-a10g": { label: "A10G", memoryGb: 24 },
  "nvidia-a30": { label: "A30", memoryGb: 24 },
  "nvidia-l40s": { label: "L40S", memoryGb: 48 },
  "nvidia-a100-40gb": { label: "A100 40GB", memoryGb: 40 },
  "nvidia-a100-80gb": { label: "A100 80GB", memoryGb: 80 },
  "nvidia-h100-80gb": { label: "H100 80GB", memoryGb: 80 },
  "nvidia-h20": { label: "H20", memoryGb: 96 },
  "nvidia-h200": { label: "H200", memoryGb: 141 },
};

export const gpuSpec = (gpuType: string): GpuSpec | undefined =>
  GPU_CATALOG[gpuType.toLowerCase()];

export type EngineConfig = {
  maxModelLen: number;
  maxNumSeqs: number;
  gpuMemoryUtilization: number;
  toolCallParser: string;
  reasoningParser: string;
  kvOffloadEnabled: boolean;
  kvOffloadRamGib: number;
  /** vLLM sleep mode: the model can sleep (weights in host RAM) to hand its GPU to another model. */
  hotSwitch: boolean;
  /** Model Streamer: concurrent safetensors reads for faster cold starts. */
  fastLoad: boolean;
};

/** GPU share kept by a hot-switch model: sleeping neighbours keep their CUDA context on the GPU. */
export const HOT_SWITCH_GPU_MEMORY_UTILIZATION = 0.8;

export const effectiveGpuMemoryUtilization = (engine: Pick<EngineConfig, "hotSwitch" | "gpuMemoryUtilization">) =>
  engine.hotSwitch
    ? Math.min(engine.gpuMemoryUtilization, HOT_SWITCH_GPU_MEMORY_UTILIZATION)
    : engine.gpuMemoryUtilization;

export type SizingInput = {
  model: CatalogModel;
  gpuType: string;
  gpuCount: number;
  engine: EngineConfig;
};

export type Sizing = {
  available: boolean;
  budgetGb: number;
  weightsGb: number;
  kvPerSequenceGb: number;
  kvRequestedGb: number;
  overheadGb: number;
  concurrentFullContext: number;
  offloadedFullContext: number;
  verdict: "fits" | "tight" | "too-large" | "unknown";
  warnings: SizingWarning[];
};

export type SizingWarning =
  | { key: "contextExceeds"; values: { max: number } }
  | { key: "tight"; values: { count: number } };

const RUNTIME_OVERHEAD_GB_PER_GPU = 1;
const GIB = 1.073741824;

// Rough planning estimate only: weights + KV cache + per-GPU runtime overhead.
export function estimateSizing({ model, gpuType, gpuCount, engine }: SizingInput): Sizing {
  const gpu = gpuSpec(gpuType);
  const warnings: SizingWarning[] = [];
  if (model.maxContext && engine.maxModelLen > model.maxContext)
    warnings.push({ key: "contextExceeds", values: { max: model.maxContext } });
  const unknown: Sizing = {
    available: false,
    budgetGb: 0,
    weightsGb: model.weightsGb,
    kvPerSequenceGb: 0,
    kvRequestedGb: 0,
    overheadGb: 0,
    concurrentFullContext: 0,
    offloadedFullContext: 0,
    verdict: "unknown",
    warnings,
  };
  if (!gpu || !model.weightsGb) return unknown;
  const budgetGb = gpu.memoryGb * gpuCount * effectiveGpuMemoryUtilization(engine);
  const weightsGb = model.weightsGb;
  const { layers, kvHeads, headDim } = model.architecture;
  // K and V, 2 bytes per element (fp16/bf16 KV cache).
  const kvBytesPerToken = 2 * layers * kvHeads * headDim * 2;
  const kvPerSequenceGb = (kvBytesPerToken * engine.maxModelLen) / 1e9;
  const kvRequestedGb = kvPerSequenceGb * engine.maxNumSeqs;
  const overheadGb = RUNTIME_OVERHEAD_GB_PER_GPU * gpuCount;
  const free = budgetGb - weightsGb - overheadGb;
  const concurrentFullContext =
    kvPerSequenceGb > 0 ? Math.max(0, Math.floor(free / kvPerSequenceGb)) : 0;
  // Host RAM tier keeps evicted prefix blocks for reuse; it does not add
  // concurrent GPU capacity.
  const offloadedFullContext =
    engine.kvOffloadEnabled && kvPerSequenceGb > 0
      ? Math.floor((engine.kvOffloadRamGib * GIB) / kvPerSequenceGb)
      : 0;
  const verdict =
    free < kvPerSequenceGb ? "too-large" : kvRequestedGb > free ? "tight" : "fits";
  if (verdict === "tight")
    warnings.push({ key: "tight", values: { count: concurrentFullContext } });
  return {
    available: true,
    budgetGb,
    weightsGb,
    kvPerSequenceGb,
    kvRequestedGb,
    overheadGb,
    concurrentFullContext,
    offloadedFullContext,
    verdict,
    warnings,
  };
}

export type DeploymentForm = {
  name: string;
  model: CatalogModel;
  clusterId: string;
  namespace: string;
  gpuType: string;
  gpuCount: number;
  routing: "round-robin" | "kv";
  cpuRequest: string;
  cpuLimit: string;
  memoryRequest: string;
  memoryLimit: string;
  storage: string;
  engine: EngineConfig;
  llmExtras?: Record<string, unknown>;
  dynamoExtras?: Record<string, unknown>;
};

export function customModel(hfPath: string): CatalogModel {
  return {
    id: "custom",
    label: hfPath || "Custom model",
    family: "Custom",
    hfPath,
    parameters: "—",
    weightsGb: 0,
    maxContext: 0,
    architecture: { layers: 0, kvHeads: 0, headDim: 0 },
    description: "Public Hugging Face repository; sizing estimate unavailable.",
    source: "custom",
  };
}

export function defaultEngine(model: CatalogModel): EngineConfig {
  return {
    maxModelLen: Math.min(model.maxContext || 4096, 4096),
    maxNumSeqs: 16,
    gpuMemoryUtilization: 0.9,
    toolCallParser: model.toolCallParser ?? "",
    reasoningParser: model.reasoningParser ?? "",
    kvOffloadEnabled: false,
    kvOffloadRamGib: 16,
    hotSwitch: false,
    // Custom repositories may ship only .bin weights, which the streamer cannot read.
    fastLoad: model.source !== "custom",
  };
}

export function buildDeploymentPayload(form: DeploymentForm) {
  const { engine } = form;
  const { enable_auto_tool_choice: autoToolChoice, ...llmExtras } = form.llmExtras || {};
  return {
    name: form.name,
    model_source: "hf",
    model_url: form.model.hfPath,
    namespace: form.namespace,
    max_replicas: 1,
    ...(form.clusterId ? { cluster_id: form.clusterId } : {}),
    args: {
      is_llm: true,
      runtime: "dynamo-vllm",
      llm: {
        ...llmExtras,
        ...(llmExtras.tensor_parallel_size != null ? { tensor_parallel_size: form.gpuCount } : {}),
        max_model_len: engine.maxModelLen,
        max_num_seqs: engine.maxNumSeqs,
        gpu_memory_utilization: effectiveGpuMemoryUtilization(engine),
        ...(engine.toolCallParser
          ? { tool_call_parser: engine.toolCallParser, enable_auto_tool_choice: autoToolChoice ?? true }
          : {}),
        ...(engine.reasoningParser ? { reasoning_parser: engine.reasoningParser } : {}),
        ...(engine.fastLoad ? { load_format: "runai_streamer" } : {}),
      },
      dynamo: {
        ...form.dynamoExtras,
        mode: "aggregated",
        replicas: 1,
        routing: form.routing,
        kv_offload: engine.kvOffloadEnabled && !engine.hotSwitch
          ? { enabled: true, ram_gib: engine.kvOffloadRamGib }
          : { enabled: false },
        ...(engine.hotSwitch ? { sleep_mode: { enabled: true, level: 1 } } : {}),
      },
    },
    resources: {
      cpu_request: form.cpuRequest,
      cpu_limit: form.cpuLimit,
      memory_request: form.memoryRequest,
      memory_limit: form.memoryLimit,
      storage: form.storage,
      gpu_type: form.gpuType,
      gpu_count: form.gpuCount,
      autopilot: false,
    },
  };
}

export function usageSnippets(endpointUrl: string, model: string) {
  const base = openAIBaseUrl(endpointUrl);
  return {
    curl: `curl ${JSON.stringify(base + "/chat/completions")} \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer YOUR_API_KEY' \\
  -d '${JSON.stringify({ model, messages: [{ role: "user", content: "Say hello." }], max_tokens: 64 })}'`,
    python: `from openai import OpenAI

client = OpenAI(base_url=${JSON.stringify(base)}, api_key="YOUR_API_KEY")
stream = client.chat.completions.create(
    model=${JSON.stringify(model)},
    messages=[{"role": "user", "content": "Say hello."}],
    stream=True,
)
for chunk in stream:
    print(chunk.choices[0].delta.content or "", end="")`,
  };
}
